import { useMemo } from 'react'
import type { Panel } from '@/types/panels'
import type { PanelSuggestions } from '@/types/search'
import { getSectionModulus, isExactMatch, zPileFirst } from '@/utils/searchUtils'

export const usePanelSuggestions = (
  debouncedSectionModulus: string,
  modulusSuggestionsData: Panel[] | undefined,
  debouncedBendingMoment: string,
  bendingSuggestionsData: Panel[] | undefined,
) => {
  const modulusSuggestions = useMemo((): PanelSuggestions => {
    if (!debouncedSectionModulus.trim() || !modulusSuggestionsData)
      return { above: [], exact: [], below: [] }

    const searchValue = parseFloat(debouncedSectionModulus)
    const above: Panel[] = []
    const exact: Panel[] = []
    const below: Panel[] = []

    modulusSuggestionsData.forEach((panel) => {
      const modValue = getSectionModulus(panel)
      if (isExactMatch(modValue, searchValue)) {
        exact.push(panel)
      } else if (modValue > searchValue) {
        above.push(panel) // Stronger panels (higher modulus)
      } else {
        below.push(panel) // Weaker panels (lower modulus) - we won't show these
      }
    })

    above.sort((a, b) => zPileFirst(a, b) || getSectionModulus(a) - getSectionModulus(b))
    below.sort((a, b) => getSectionModulus(a) - getSectionModulus(b))
    exact.sort((a, b) => {
      const diffA = Math.abs(getSectionModulus(a) - searchValue)
      const diffB = Math.abs(getSectionModulus(b) - searchValue)
      return zPileFirst(a, b) || diffA - diffB
    })

    return { above, exact, below }
  }, [debouncedSectionModulus, modulusSuggestionsData])

  const bendingSuggestions = useMemo((): PanelSuggestions => {
    if (!debouncedBendingMoment.trim() || !bendingSuggestionsData)
      return { above: [], exact: [], below: [] }

    const searchValue = parseFloat(debouncedBendingMoment)
    const above: Panel[] = []
    const exact: Panel[] = []
    const below: Panel[] = []

    bendingSuggestionsData.forEach((panel: Panel) => {
      const bendingValue = panel.bending_moment || 0
      if (isExactMatch(bendingValue, searchValue)) {
        exact.push(panel)
      } else if (bendingValue > searchValue) {
        above.push(panel) // Stronger panels (higher bending moment)
      } else {
        below.push(panel) // Weaker panels (lower bending moment) - we won't show these
      }
    })

    above.sort((a, b) => zPileFirst(a, b) || (a.bending_moment || 0) - (b.bending_moment || 0))
    below.sort((a, b) => (a.bending_moment || 0) - (b.bending_moment || 0))
    exact.sort((a, b) => {
      const diffA = Math.abs((a.bending_moment || 0) - searchValue)
      const diffB = Math.abs((b.bending_moment || 0) - searchValue)
      return zPileFirst(a, b) || diffA - diffB
    })

    return { above, exact, below }
  }, [debouncedBendingMoment, bendingSuggestionsData])

  return {
    modulusSuggestions,
    bendingSuggestions,
  }
}
